/**
 * WordPress Interactivity API - Navigation Menu with Nested Drawers
 * Enhanced with smooth sequential animations and proper state management
 *
 * @package AltenTechnology
 */

import { store, getElement, getContext, withSyncEvent } from '@wordpress/interactivity';
import './view.scss';

// Animation timing constants (must match CSS)
const ANIMATION_TIMING = {
	MAIN_DRAWER: 300,      // Main drawer slide animation
	SUBMENU: 400,          // Submenu slide animation
	ITEM_STAGGER: 30,      // Stagger delay between items
	BUFFER: 50,            // Small buffer for animation completion
	FOCUS_DELAY: 100,      // Delay before focusing elements
};

// Shared store for navigation drawer and menu functionality
const navDrawerStore = store('dapd/nav-drawer', {
	state: {
		isOpen: false,
		openDrawers: [],
		isAnimating: false,  // Animation lock to prevent race conditions
		animationQueue: [],  // Queue for sequential animations
	},

	actions: {
		/**
		 * Wait for animation to complete
		 * Returns a promise that resolves after the specified duration
		 */
		waitForAnimation(duration) {
			return new Promise(resolve => {
				// Use requestAnimationFrame to ensure smooth animations
				requestAnimationFrame(() => {
					setTimeout(resolve, duration);
				});
			});
		},

		/**
		 * Toggle main drawer open/closed
		 */
		async toggle() {
			const { state, actions } = navDrawerStore;

			// Prevent toggle during animation
			if (state.isAnimating) return;

			state.isAnimating = true;
			state.isOpen = !state.isOpen;

			if (state.isOpen) {
				document.body.classList.add('nav-drawer-open');

				// Wait for drawer to open before focusing
				await actions.waitForAnimation(ANIMATION_TIMING.FOCUS_DELAY);

				const drawer = document.querySelector('.is-style-nav-drawer');
				if (drawer) {
					const focusable = drawer.querySelector(
						'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
					);
					if (focusable) {
						focusable.focus();
					}
				}
			} else {
				document.body.classList.remove('nav-drawer-open');
				state.openDrawers = [];

				// Wait for main drawer animation to complete
				await actions.waitForAnimation(ANIMATION_TIMING.MAIN_DRAWER);
			}

			state.isAnimating = false;
		},

		/**
		 * Close main drawer immediately
		 */
		async close(skipPointerEvents = false) {
			const { state, actions } = navDrawerStore;

			if (!state.isOpen && state.openDrawers.length === 0) return;

			// Disable pointer events on nav to prevent hover interference during close
			// (unless called from closeAll which handles it)
			const navElement = document.querySelector('[data-hover-enabled="true"]');
			if (navElement && !skipPointerEvents) {
				navElement.style.pointerEvents = 'none';
			}

			state.isOpen = false;
			state.openDrawers = [];
			document.body.classList.remove('nav-drawer-open');

			// Wait for animation to complete
			await actions.waitForAnimation(ANIMATION_TIMING.MAIN_DRAWER);

			// Re-enable pointer events after closing (unless closeAll handles it)
			if (navElement && !skipPointerEvents) {
				navElement.style.pointerEvents = '';
			}
		},

		/**
		 * Close all drawers and submenus sequentially with smooth animation
		 * Closes from deepest submenu to main drawer
		 */
		async closeAll() {
			const { state, actions } = navDrawerStore;

			// Prevent multiple simultaneous closeAll operations
			if (state.isAnimating) return;

			state.isAnimating = true;

			// Disable pointer events on nav to prevent hover interference during close
			const navElement = document.querySelector('[data-hover-enabled="true"]');
			if (navElement) {
				navElement.style.pointerEvents = 'none';
			}

			try {
				// Close submenus one by one from deepest to shallowest
				while (state.openDrawers.length > 0) {
					await actions.closeSubmenuAnimated();
				}

				// All submenus closed, now close main drawer if open
				if (state.isOpen) {
					await actions.close(true); // Skip pointer events handling since we're handling it here
				}
			} finally {
				state.isAnimating = false;

				// Re-enable pointer events after closing
				if (navElement) {
					navElement.style.pointerEvents = '';
				}
			}
		},

		/**
		 * Close a submenu with proper animation timing
		 */
		async closeSubmenuAnimated() {
			const { state, actions } = navDrawerStore;

			if (state.openDrawers.length === 0) return;

			const closingDrawerId = state.openDrawers[state.openDrawers.length - 1];
			const menuItem = document.getElementById(closingDrawerId);

			// Update aria attributes immediately for accessibility
			if (menuItem) {
				const link = menuItem.querySelector('.wp-block-nav-menu__item-link');
				if (link) {
					link.setAttribute('aria-expanded', 'false');
				}

				const toggleButton = menuItem.querySelector('.wp-block-nav-menu__submenu-toggle');
				if (toggleButton) {
					toggleButton.setAttribute('aria-expanded', 'false');
				}
			}

			// Remove from state to trigger CSS animation
			state.openDrawers = state.openDrawers.slice(0, -1);

			// Wait for CSS animation to complete
			await actions.waitForAnimation(ANIMATION_TIMING.SUBMENU);

			// Focus the toggle button after animation
			if (menuItem) {
				const toggleButton = menuItem.querySelector('.wp-block-nav-menu__submenu-toggle');
				if (toggleButton && document.activeElement !== toggleButton) {
					toggleButton.focus();
				}
			}
		},

		/**
		 * Open a submenu drawer with smooth animation
		 */
		openSubmenu: withSyncEvent(async (event) => {
			event.preventDefault();
			event.stopPropagation();

			const { state, actions } = navDrawerStore;

			// Allow opening during animation for hover mode, but prevent spam clicks
			// In hover mode, we want immediate response
			const isFromHover = event.target.closest('[data-hover-enabled="true"]');
			if (state.isAnimating && !isFromHover) return;

			const element = getElement();
			const menuItem = element.ref.closest('.wp-block-nav-menu__item');

			if (!menuItem) {
				console.log('❌ openSubmenu: No menu item found');
				return;
			}

			const menuItemId = menuItem.id;
			console.log('🔵 openSubmenu called for:', menuItemId);

			// Get all parent menu item IDs (traverse up the tree)
			const parentIds = [];
			let currentElement = menuItem.parentElement; // Start with parent <ul>

			while (currentElement) {
				// Check if we're inside a submenu
				if (currentElement.classList.contains('wp-block-nav-menu__submenu')) {
					// Get the parent menu item of this submenu
					const parentMenuItem = currentElement.closest('.wp-block-nav-menu__item');
					if (parentMenuItem && parentMenuItem.id) {
						parentIds.unshift(parentMenuItem.id); // Add to beginning (root first)
					}
				}

				currentElement = currentElement.parentElement;
			}

			// Check if we need to update the open drawers
			const currentPath = [...parentIds, menuItemId];

			// Check if the current path is different from the open drawers
			// Need to open if: lengths don't match OR any ID is different
			const pathsMatch =
				state.openDrawers.length === currentPath.length &&
				state.openDrawers.every((id, index) => id === currentPath[index]);

			const needsUpdate = !pathsMatch;

			console.log('📋 Parent IDs:', parentIds);
			console.log('📋 Current path:', currentPath);
			console.log('📋 Current openDrawers:', state.openDrawers);
			console.log('📋 Paths match:', pathsMatch);
			console.log('📋 Needs update:', needsUpdate);

			// Always update path for hover mode to ensure submenu opens
			if (needsUpdate || isFromHover) {
				const needsAnimation = needsUpdate && !isFromHover;
				if (needsAnimation) {
					state.isAnimating = true;
				}

				// Close any sibling branches by setting the new path
				state.openDrawers = currentPath;
				console.log('✅ Updated openDrawers to:', state.openDrawers);

				// Update aria attributes
				const parentLink = menuItem.querySelector('.wp-block-nav-menu__item-link');
				if (parentLink) {
					parentLink.setAttribute('aria-expanded', 'true');
				}

				const toggleButton = menuItem.querySelector('.wp-block-nav-menu__submenu-toggle');
				if (toggleButton) {
					toggleButton.setAttribute('aria-expanded', 'true');
				}

				// Wait for opening animation (skip for hover mode)
				if (needsAnimation && !isFromHover) {
					await actions.waitForAnimation(ANIMATION_TIMING.SUBMENU + ANIMATION_TIMING.BUFFER);

					// Focus first item in submenu (only for click interactions)
					const submenu = menuItem.querySelector('.wp-block-nav-menu__submenu');
					if (submenu) {
						const firstFocusable = submenu.querySelector(
							'a, button, [tabindex]:not([tabindex="-1"])',
						);
						if (firstFocusable) {
							firstFocusable.focus();
						}
					}

					state.isAnimating = false;
				}
			}
		}),

		/**
		 * Close the most recently opened submenu (used for ESC key)
		 */
		async closeSubmenu() {
			const { state, actions } = navDrawerStore;

			if (state.openDrawers.length === 0) return;

			// Prevent closing during animation
			if (state.isAnimating) return;

			state.isAnimating = true;
			await actions.closeSubmenuAnimated();
			state.isAnimating = false;
		},

		/**
		 * Handle keyboard navigation
		 */
		async handleEscape() {
			const { state, actions } = navDrawerStore;

			// Prevent action during animation
			if (state.isAnimating) return;

			if (state.openDrawers.length > 0) {
				await actions.closeSubmenu();
			} else if (state.isOpen) {
				await actions.close();
			}
		},
	},

	callbacks: {
		/**
		 * Initialize drawer - set up keyboard handlers
		 */
		init() {
			const { actions } = navDrawerStore;

			const handleKeydown = async (event) => {
				if (event.key === 'Escape') {
					event.preventDefault();
					await actions.handleEscape();
				}
			};

			document.addEventListener('keydown', handleKeydown);

			return () => {
				document.removeEventListener('keydown', handleKeydown);
			};
		},

		/**
		 * Initialize menu - set up hover behavior if enabled
		 */
		initMenu() {
			const element = getElement();
			const context = getContext();
			const navElement = element.ref;

			if (!navElement || !context) return;

			// Check if hover is enabled and we're on desktop
			const isDesktop = window.matchMedia('(min-width: 1024px)').matches;
			const hoverEnabled = context.hoverOnDesktop && isDesktop;

			if (hoverEnabled) {
				// Add data attribute for CSS styling
				navElement.setAttribute('data-hover-enabled', 'true');

				const { state } = navDrawerStore;

				// Helper function to close sibling submenus
				const closeSiblingSubmenus = (currentItem) => {
					const parent = currentItem.parentElement;
					if (!parent) return;

					// Get all sibling items with submenus at this level
					const siblingItems = parent.querySelectorAll(':scope > .wp-block-nav-menu__item--has-submenu');

					siblingItems.forEach((sibling) => {
						if (sibling !== currentItem) {
							const siblingId = sibling.id;

							// Remove from openDrawers if present
							if (siblingId && state.openDrawers.includes(siblingId)) {
								state.openDrawers = state.openDrawers.filter(id => id !== siblingId);
							}

							// Update aria attributes
							const siblingToggle = sibling.querySelector('.wp-block-nav-menu__submenu-toggle');
							if (siblingToggle) {
								siblingToggle.setAttribute('aria-expanded', 'false');
							}
							const siblingLink = sibling.querySelector('.wp-block-nav-menu__item-link');
							if (siblingLink) {
								siblingLink.setAttribute('aria-expanded', 'false');
							}
						}
					});
				};

				// Add hover event listeners to ALL menu items (with and without submenus)
				const allMenuItems = navElement.querySelectorAll('.wp-block-nav-menu__item');

				allMenuItems.forEach((menuItem) => {
					let hoverTimeout = null;
					let isHovering = false;
					const hasSubmenu = menuItem.classList.contains('wp-block-nav-menu__item--has-submenu');

					// Mouse enter
					menuItem.addEventListener('mouseenter', () => {
						isHovering = true;
						clearTimeout(hoverTimeout);

						hoverTimeout = setTimeout(() => {
							if (!isHovering) return;

							// Close sibling submenus for ALL items
							closeSiblingSubmenus(menuItem);

							// If this item has a submenu, open it
							if (hasSubmenu) {
								const toggleButton = menuItem.querySelector('.wp-block-nav-menu__submenu-toggle');
								if (toggleButton && toggleButton.getAttribute('aria-expanded') !== 'true') {
									toggleButton.click();
								}
							}
						}, 150); // Short delay to prevent accidental opens
					});

					// Mouse leave - cancel pending open
					menuItem.addEventListener('mouseleave', () => {
						isHovering = false;
						clearTimeout(hoverTimeout);
					});
				});

				// Close all submenus when cursor leaves the navigation
				navElement.addEventListener('mouseleave', () => {
					// Close all open submenus
					if (state.openDrawers.length > 0) {
						// Get all items that currently have submenus open
						state.openDrawers.forEach((drawerId) => {
							const menuItem = document.getElementById(drawerId);
							if (menuItem) {
								const toggleButton = menuItem.querySelector('.wp-block-nav-menu__submenu-toggle');
								if (toggleButton) {
									toggleButton.setAttribute('aria-expanded', 'false');
								}
								const link = menuItem.querySelector('.wp-block-nav-menu__item-link');
								if (link) {
									link.setAttribute('aria-expanded', 'false');
								}
							}
						});

						// Clear all open drawers
						state.openDrawers = [];
					}
				});
			}
		},

		/**
		 * Initialize submenu - set up context watching
		 */
		initSubmenu() {
			const context = getContext();
			const { state } = navDrawerStore;

			if (!context || !context.menuItemId) {
				return;
			}

			// Set initial open state in context
			context.isOpen = state.openDrawers.includes(context.menuItemId);
		},

		/**
		 * Watch state changes and update submenu open state
		 */
		watchSubmenu() {
			const context = getContext();
			const { state } = navDrawerStore;

			if (!context || !context.menuItemId) {
				// console.log('⚠️ watchSubmenu: No context or menuItemId');
				return;
			}

			// Update context based on state
			const shouldBeOpen = state.openDrawers.includes(context.menuItemId);
			// console.log(`👁️ watchSubmenu for ${context.menuItemId}: shouldBeOpen=${shouldBeOpen}, openDrawers=`, state.openDrawers);
			context.isOpen = shouldBeOpen;
		},
	},
});
